import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';


const PaymentProcessingPanel = ({ subscriptions, onRefresh }) => {
  const { currentTenant } = useAuth();
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    subscriptionId: '',
    amount: '',
    paymentMethod: 'credit_card',
    notes: '',
    cashTendered: '',
    changeGiven: ''
  });

  const activeSubscriptions = subscriptions?.filter(s => s?.status === 'active') || [];

  const paymentMethodOptions = [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
    { value: 'other', label: 'Other' }
  ];

  const calculateChange = () => {
    if (formData?.paymentMethod === 'cash' && formData?.cashTendered && formData?.amount) {
      const tendered = parseFloat(formData?.cashTendered);
      const amount = parseFloat(formData?.amount);
      return Math.max(0, tendered - amount);
    }
    return 0;
  };

  const handleProcessPayment = async (e) => {
    e?.preventDefault();
    
    // Validate cash payment
    if (formData?.paymentMethod === 'cash') {
      if (!formData?.cashTendered || parseFloat(formData?.cashTendered) < parseFloat(formData?.amount)) {
        alert('Cash tendered must be greater than or equal to payment amount');
        return;
      }
    }
    
    setProcessing(true);

    try {
      const subscription = activeSubscriptions?.find(s => s?.id === formData?.subscriptionId);
      if (!subscription) throw new Error('Subscription not found');

      const invoiceNumber = `INV-${Date.now()}`;
      const billingDate = new Date()?.toISOString()?.split('T')?.[0];
      const dueDate = new Date();
      dueDate?.setDate(dueDate?.getDate() + 7);

      const paymentNotes = formData?.paymentMethod === 'cash' && calculateChange() > 0
        ? `${formData?.notes || ''} | Cash Tendered: $${parseFloat(formData?.cashTendered)?.toFixed(2)} | Change: $${calculateChange()?.toFixed(2)}`
        : formData?.notes;

      const { error } = await supabase?.from('billing_records')?.insert([{
          tenant_id: currentTenant?.id,
          subscription_id: formData?.subscriptionId,
          member_id: subscription?.member_id,
          invoice_number: invoiceNumber,
          billing_date: billingDate,
          due_date: dueDate?.toISOString()?.split('T')?.[0],
          amount: parseFloat(formData?.amount),
          tax_amount: 0,
          total_amount: parseFloat(formData?.amount),
          payment_status: 'completed',
          payment_date: new Date()?.toISOString(),
          payment_method: formData?.paymentMethod,
          transaction_id: `TXN-${Date.now()}`,
          payment_notes: paymentNotes
        }]);

      if (error) throw error;

      setShowProcessModal(false);
      setFormData({ subscriptionId: '', amount: '', paymentMethod: 'credit_card', notes: '', cashTendered: '', changeGiven: '' });
      onRefresh?.();
      
      if (formData?.paymentMethod === 'cash' && calculateChange() > 0) {
        alert(`Payment processed successfully! Change due: $${calculateChange()?.toFixed(2)}`);
      } else {
        alert('Payment processed successfully!');
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      alert(err?.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
            <Icon name="DollarSign" size={24} color="var(--color-success)" />
            Payment Processing
          </h2>
        </div>

        <div className="space-y-4">
          <Button
            onClick={() => setShowProcessModal(true)}
            iconName="Plus"
            iconPosition="left"
            fullWidth
          >
            Process Manual Payment
          </Button>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <h3 className="font-medium text-foreground">Manual Charges</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Process one-time payments or additional charges
              </p>
              <Button variant="outline" size="sm" fullWidth>
                View History
              </Button>
            </div>

            <div className="p-4 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="RotateCcw" size={20} color="var(--color-warning)" />
                <h3 className="font-medium text-foreground">Refund Processing</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Issue refunds and process chargebacks
              </p>
              <Button variant="outline" size="sm" fullWidth>
                Process Refund
              </Button>
            </div>
          </div>

          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Payment Processing Tips</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Always verify member identity before processing</li>
                  <li>• Include detailed notes for audit trails</li>
                  <li>• Refunds typically process within 5-7 business days</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Process Payment Modal */}
      {showProcessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-bold text-foreground">Process Manual Payment</h2>
              <button
                onClick={() => setShowProcessModal(false)}
                className="text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleProcessPayment} className="p-6">
              <div className="space-y-4">
                <Select
                  label="Select Subscription"
                  options={activeSubscriptions?.map(sub => ({
                    value: sub?.id,
                    label: `${sub?.user_profiles?.full_name} - ${sub?.membership_plans?.plan_name}`
                  }))}
                  value={formData?.subscriptionId}
                  onChange={(value) => setFormData({ ...formData, subscriptionId: value })}
                  required
                />

                <Input
                  label="Payment Amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData?.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e?.target?.value })}
                  required
                />

                <Select
                  label="Payment Method"
                  options={paymentMethodOptions}
                  value={formData?.paymentMethod}
                  onChange={(value) => setFormData({ ...formData, paymentMethod: value, cashTendered: '', changeGiven: '' })}
                  required
                />

                {/* Cash-specific fields */}
                {formData?.paymentMethod === 'cash' && (
                  <>
                    <Input
                      label="Cash Tendered"
                      type="number"
                      step="0.01"
                      min={formData?.amount || 0}
                      placeholder="0.00"
                      value={formData?.cashTendered}
                      onChange={(e) => setFormData({ ...formData, cashTendered: e?.target?.value })}
                      required
                    />
                    
                    {formData?.cashTendered && calculateChange() > 0 && (
                      <div className="p-3 bg-warning/10 rounded-lg border border-warning/20">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">Change Due:</span>
                          <span className="text-lg font-bold text-warning">${calculateChange()?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <Input
                  label="Notes"
                  placeholder="Add payment notes..."
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                />
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <Button type="button" variant="ghost" onClick={() => setShowProcessModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? 'Processing...' : 'Process Payment'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentProcessingPanel;